// frontend/src/components/Charts/TopPerformersChart.jsx
// Barra horizontal genérica (estilo Argon) para rankings: Top Vendedores / Top Clientes.
// Props:
//   data     -> [{ label, value }]  (value = monto en MXN, sin IVA)
//   title    -> texto opcional del eje/tooltip (ej. "Órdenes")
//   colorRgb -> "r, g, b" de la paleta Argon (default azul)

import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const currency = (value) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);

const truncate = (text, max = 22) => {
  const str = String(text ?? '');
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};

const TopPerformersChart = ({ data = [], title = 'Monto', colorRgb = '94, 114, 228' }) => {
  const rows = (data || []).filter((d) => d && (d.value || 0) > 0);

  if (rows.length === 0) {
    return (
      <div
        style={{
          height: '260px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: '#8898aa'
        }}
      >
        <i className="ni ni-chart-bar-32" style={{ fontSize: '3rem', marginBottom: '1rem' }}></i>
        <p className="mb-0">No hay datos disponibles</p>
        <small>Genera actividad para ver el ranking</small>
      </div>
    );
  }

  const chartData = {
    labels: rows.map((r) => truncate(r.label)),
    datasets: [
      {
        label: title,
        data: rows.map((r) => r.value || 0),
        backgroundColor: `rgba(${colorRgb}, 0.8)`,
        borderColor: `rgb(${colorRgb})`,
        borderWidth: 1,
        borderRadius: 4,
        maxBarThickness: 26
      }
    ]
  };

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        callbacks: {
          title: (items) => rows[items[0].dataIndex]?.label ?? '',
          label: (context) => `${title}: ${currency(context.parsed.x)}`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { borderDash: [2, 2], color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { callback: (value) => currency(value) }
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 12 } }
      }
    }
  };

  const height = Math.max(220, rows.length * 46 + 40);

  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default TopPerformersChart;
